import { withJwt } from '../../../utils/with-jwt';
import { requiredHeaders } from '../../../models/required-headers';
import { auditLogEnvironment } from '../audit-log-environment';
import { selectData } from '../../../utils/select-data';
import { parseRequestSchema } from '../../../utils/parse-request-schema';
import { dateToISOStringPreprocessor } from '../../../utils/date-to-iso-string-preprocessor';
import { auditLogSchema } from '../schemas/audit-log.schema';
import { fromFetch } from 'rxjs/fetch';
import { z } from 'zod/v4';

export const requestSchema = z.object({
  accountId: z.string(),
  requestOrigin: z.optional(z.string()),
  operation: z.optional(z.string()),
  startTimestamp: z.coerce.date(),
  endTimestamp: z.coerce.date(),
  offset: z.array(z.union([z.string(), z.number()])).optional(),
  sort: z.array(
    z.object({
      field: z.string(),
      order: z.union([z.literal(-1), z.literal(0), z.literal(1)]),
    })
  ),
  size: z.number(),
  verbose: z.boolean().optional(),
  requestSearchText: z.string().optional(),
  responseSearchText: z.string().optional(),
});

/**
 * Requests CoPilot Logs in a paginated format
 * @param partnerId Partner ID
 * @param request.accountId Account ID
 * @param request.operation Specific operation you are requesting, null if none
 * @param request.requestOrigin Specify api origin you are requesting, null if none
 * @param request.startTimestamp Start date/time of request
 * @param request.endTimestamp End date/time of request
 * @param request.offset Offset of paginated request
 * @param request.sorts Sorting parameters of request
 * @param request.size Number of logs being requested
 * @param request.requestSearchText Search text for request
 * @param request.responseSearchText Search text for response
 * @returns An object containing an array of the requested audit logs
 */
export function postAuditLogSearch(
  this: unknown,
  partnerId: string,
  request: z.infer<typeof requestSchema>
) {
  return withJwt((jwtAuthHeaders) =>
    fromFetch(
      new Request(
        auditLogEnvironment.resolve(this).auditlogSearchUrl(partnerId),
        {
          method: 'POST',
          headers: {
            ...requiredHeaders,
            ...jwtAuthHeaders,
          },
          body: parseRequestSchema(
            requestSchema.extend({
              startTimestamp: dateToISOStringPreprocessor(),
              endTimestamp: dateToISOStringPreprocessor(),
            }),
            request
          ),
        }
      )
    )
  ).pipe(
    selectData(
      z.object({
        totalCount: z.number(),
        data: z.array(auditLogSchema),
      })
    )
  );
}
